<script setup lang="ts">
/**
 * Module: web/src/views/admin/AdminErrorsView.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.0
 * Date: 2026-07-14
 *
 * Admin ERRORS view (contract error-observability-api v1.0.0 SS7/SS8;
 * finding #2326 + scratch #913 -- Robert's spec: user / time / page /
 * interaction / details, client + server rows interleaved newest-first).
 * Modeled on AdminAuditView: same user_admin gate, same card/table atoms,
 * wide table scrolls inside its own container on mobile (doc 12 SS4.1
 * tier-2 rule). Read-only in v1 -- no mutating action, no audit vocabulary.
 *
 *   - filters: source / kind / user / from / to / free-text q (message,
 *     page, interaction), all server-side (GET /admin/errors)
 *   - counts chips describe the FILTERED set (contract SS7)
 *   - a row expands to the pretty-printed detail JSON (stack excerpt,
 *     method+path+status, ua, app shell hash, burst count)
 *   - pager over total (page/page_size)
 */
import { onMounted, ref, watch } from 'vue';
import { apiGet } from '@/composables/useApi';
import type { AdminErrorsResponse, AdminUser, AppErrorRow } from '@/types/api';
import { fmtDateTime } from '@/lib/format';
import HelpTip from '@/components/HelpTip.vue';

const KINDS = [
  'code_error', 'unhandled_rejection', 'api_error', 'request_failed',
  'http_4xx', 'http_5xx', 'exception',
];

const rows = ref<AppErrorRow[]>([]);
const total = ref(0);
const counts = ref<AdminErrorsResponse['counts']>({ source: {}, kind: {} });
const loading = ref(true);
const errorMessage = ref('');

const source = ref('');
const kind = ref('');
const userId = ref('');
const from = ref('');
const to = ref('');
const q = ref('');
const page = ref(1);
const pageSize = ref(50);

const users = ref<AdminUser[]>([]);
const expanded = ref(new Set<number>());

/** client rows carry the accent tone (they are the never-arrived/FE story);
 *  server rows stay neutral -- the chip pair reads at a glance. */
function sourceChip(s: string): string {
  return s === 'client' ? 'chip chip-accent' : 'chip chip-neutral';
}

/** Severity tone per kind: code bugs + 5xx red, never-reached warm, the
 *  server-said-no classes accent. */
function kindChip(k: string): string {
  if (k === 'code_error' || k === 'unhandled_rejection' || k === 'exception'
      || k === 'http_5xx') return 'chip chip-bad';
  if (k === 'request_failed') return 'chip chip-warm';
  return 'chip chip-accent';
}

function buildQuery(): string {
  const params = new URLSearchParams();
  if (source.value) params.set('source', source.value);
  if (kind.value) params.set('kind', kind.value);
  if (userId.value) params.set('user_id', userId.value);
  if (from.value) params.set('from', from.value);
  if (to.value) params.set('to', to.value);
  if (q.value.trim()) params.set('q', q.value.trim());
  params.set('page', String(page.value));
  params.set('page_size', String(pageSize.value));
  return params.toString();
}

async function load() {
  loading.value = true;
  errorMessage.value = '';
  try {
    const body = await apiGet<AdminErrorsResponse>('/admin/errors', buildQuery());
    rows.value = body.errors ?? [];
    total.value = body.total ?? 0;
    counts.value = body.counts ?? { source: {}, kind: {} };
    expanded.value = new Set();
  } catch {
    errorMessage.value = 'Could not load the error log.';
  } finally {
    loading.value = false;
  }
}

async function loadUsers() {
  try {
    users.value = await apiGet<AdminUser[]>('/admin/users');
  } catch {
    users.value = []; // filter degrades to "any user"; the table still works
  }
}

function applyFilters() {
  page.value = 1;
  void load();
}

function clearFilters() {
  source.value = '';
  kind.value = '';
  userId.value = '';
  from.value = '';
  to.value = '';
  q.value = '';
  applyFilters();
}

function toggle(id: number) {
  const next = new Set(expanded.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expanded.value = next;
}

function detailJson(row: AppErrorRow): string {
  try {
    return JSON.stringify(row.detail ?? {}, null, 2);
  } catch {
    return String(row.detail);
  }
}

function userLabel(row: AppErrorRow): string {
  if (row.user_name) return row.user_name;
  if (row.user_id !== null && row.user_id !== undefined) return `user #${row.user_id}`;
  if (row.session_kind === 'pin') return 'PIN session';
  return '—';
}

const lastPage = () => Math.max(1, Math.ceil(total.value / pageSize.value));

function prevPage() {
  if (page.value > 1) { page.value -= 1; void load(); }
}
function nextPage() {
  if (page.value < lastPage()) { page.value += 1; void load(); }
}

// Selects/dates apply immediately; free text applies debounced.
watch([source, kind, userId, from, to, pageSize], applyFilters);
let qTimer: number | null = null;
watch(q, () => {
  if (qTimer !== null) window.clearTimeout(qTimer);
  qTimer = window.setTimeout(applyFilters, 400);
});

onMounted(() => {
  void load();
  void loadUsers();
});
</script>

<template>
  <div class="px-4 py-5 md:p-6 max-w-6xl">
    <div class="flex items-start justify-between gap-3 mb-4 flex-wrap">
      <div>
        <h1 class="text-xl font-bold text-fg flex items-center gap-1.5">
          Errors
          <HelpTip k="admin.errors" />
        </h1>
        <p class="text-sm text-fg-muted">
          App errors from both sides — browser reports (delivered even after
          connection loss) and server-side failures.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <span v-if="!loading" class="text-xs text-fg-muted whitespace-nowrap">
          {{ (counts.source.client ?? 0) }} client · {{ (counts.source.server ?? 0) }} server
        </span>
        <button class="btn-secondary !h-11 !px-3 text-sm" :disabled="loading" @click="load">Refresh</button>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-card border border-border-c rounded-lg p-3 mb-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 items-end">
      <label class="block">
        <span class="block text-xs text-fg-muted mb-1">Source</span>
        <select v-model="source" class="field-input !h-11 text-sm">
          <option value="">All</option>
          <option value="client">client</option>
          <option value="server">server</option>
        </select>
      </label>
      <label class="block">
        <span class="block text-xs text-fg-muted mb-1">Kind</span>
        <select v-model="kind" class="field-input !h-11 text-sm">
          <option value="">All</option>
          <option v-for="k in KINDS" :key="k" :value="k">{{ k }}</option>
        </select>
      </label>
      <label class="block">
        <span class="block text-xs text-fg-muted mb-1">User</span>
        <select v-model="userId" class="field-input !h-11 text-sm">
          <option value="">Anyone</option>
          <option v-for="u in users" :key="u.id" :value="String(u.id)">{{ u.name }}</option>
        </select>
      </label>
      <label class="block">
        <span class="block text-xs text-fg-muted mb-1">From</span>
        <input v-model="from" type="date" class="field-input !h-11 text-sm" />
      </label>
      <label class="block">
        <span class="block text-xs text-fg-muted mb-1">To</span>
        <input v-model="to" type="date" class="field-input !h-11 text-sm" />
      </label>
      <label class="block col-span-2 md:col-span-2">
        <span class="block text-xs text-fg-muted mb-1">Search</span>
        <div class="flex gap-2">
          <input
            v-model="q"
            type="search"
            placeholder="message, page, interaction…"
            class="field-input !h-11 text-sm flex-1"
          />
          <button class="btn-secondary !h-11 !px-3 text-sm" @click="clearFilters">Clear</button>
        </div>
      </label>
    </div>

    <div v-if="errorMessage" class="alert-error mb-4" role="alert">{{ errorMessage }}</div>
    <div v-if="loading" class="text-sm text-fg-muted py-8 text-center">Loading…</div>

    <div v-else-if="rows.length === 0" class="bg-card border border-border-c rounded-lg p-8 text-center">
      <p class="text-sm text-fg-muted">No errors match — good news, or fresh filters.</p>
    </div>

    <div v-else class="bg-card border border-border-c rounded-lg overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs uppercase tracking-wide text-fg-muted border-b border-border-c">
              <th class="px-4 py-3 font-medium">When</th>
              <th class="px-4 py-3 font-medium">Source</th>
              <th class="px-4 py-3 font-medium">Kind</th>
              <th class="px-4 py-3 font-medium">User</th>
              <th class="px-4 py-3 font-medium">Page</th>
              <th class="px-4 py-3 font-medium">Interaction</th>
              <th class="px-4 py-3 font-medium">Message</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in rows" :key="row.id">
              <tr
                class="border-b border-border-c/60 last:border-0 hover:bg-input/40 align-top cursor-pointer"
                :aria-expanded="expanded.has(row.id)"
                @click="toggle(row.id)"
              >
                <td class="px-4 py-2.5 text-fg whitespace-nowrap">{{ fmtDateTime(row.occurred_at) }}</td>
                <td class="px-4 py-2.5 whitespace-nowrap"><span :class="sourceChip(row.source)">{{ row.source }}</span></td>
                <td class="px-4 py-2.5 whitespace-nowrap"><span :class="kindChip(row.kind)">{{ row.kind }}</span></td>
                <td class="px-4 py-2.5 text-fg-muted whitespace-nowrap">{{ userLabel(row) }}</td>
                <td class="px-4 py-2.5 text-fg-muted whitespace-nowrap max-w-[12rem] truncate">{{ row.page ?? '—' }}</td>
                <td class="px-4 py-2.5 text-fg-muted whitespace-nowrap max-w-[10rem] truncate">{{ row.interaction ?? '—' }}</td>
                <td class="px-4 py-2.5 text-fg-muted min-w-[16rem] max-w-[28rem]">
                  <span class="line-clamp-2">{{ row.message }}</span>
                </td>
              </tr>
              <tr v-if="expanded.has(row.id)" class="border-b border-border-c/60 last:border-0 bg-input/20">
                <td colspan="7" class="px-4 py-3">
                  <div class="text-xs text-fg-muted mb-1.5">
                    received {{ fmtDateTime(row.received_at) }}
                    <template v-if="row.client_error_id"> · beacon id <code class="text-[11px]">{{ row.client_error_id }}</code></template>
                    <template v-if="row.session_kind"> · {{ row.session_kind }} session</template>
                  </div>
                  <pre class="text-xs text-fg bg-bg border border-border-c rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">{{ detailJson(row) }}</pre>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Pager -->
      <div class="flex items-center justify-between gap-2 px-4 py-3 border-t border-border-c text-sm">
        <div class="text-fg-muted">
          {{ total }} error{{ total === 1 ? '' : 's' }} · page {{ page }} of {{ lastPage() }}
        </div>
        <div class="flex items-center gap-2">
          <select v-model.number="pageSize" class="field-input !h-9 !w-auto text-sm">
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="200">200</option>
          </select>
          <button class="btn-secondary !h-9 !px-3 text-sm" :disabled="page <= 1 || loading" @click="prevPage">Prev</button>
          <button class="btn-secondary !h-9 !px-3 text-sm" :disabled="page >= lastPage() || loading" @click="nextPage">Next</button>
        </div>
      </div>
    </div>
  </div>
</template>
